import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Trophy, ChevronRight } from "lucide-react";
import { locationScore } from "@/lib/helpers";

const MEDAL = ["text-amber-500", "text-zinc-400", "text-amber-700"];

export default function LocationScoreboard({ locations, elements }) {
  const navigate = useNavigate();

  const ranked = useMemo(() => {
    return locations
      .filter((l) => (l.status || "potential") === "potential")
      .map((l) => {
        const { score, rated } = locationScore(l, elements);
        return { ...l, score, rated };
      })
      .sort((a, b) => b.score - a.score);
  }, [locations, elements]);

  return (
    <div className="mb-6 border border-zinc-200 bg-white" data-testid="location-scoreboard">
      <div className="flex items-center justify-between border-b border-zinc-200 px-4 py-3">
        <p className="flex items-center gap-2 font-heading text-sm font-semibold tracking-tight text-zinc-900">
          <Trophy className="h-4 w-4 text-[#b32233]" /> Potential Location Scoreboard
        </p>
        <span className="text-xs text-zinc-400">
          Ranked by overall score (out of 10) · {ranked.length} potential
        </span>
      </div>

      {elements.length === 0 ? (
        <p className="px-4 py-6 text-sm text-zinc-400">
          No scorecard elements defined yet. Add them in Settings → Locations to rank potential sites.
        </p>
      ) : ranked.length === 0 ? (
        <p className="px-4 py-6 text-sm text-zinc-400">No potential locations to rank yet.</p>
      ) : (
        <div className="divide-y divide-zinc-100">
          {ranked.map((l, i) => (
            <button
              key={l.id}
              onClick={() => navigate(`/locations/${l.id}`)}
              data-testid={`scoreboard-row-${l.id}`}
              className="flex w-full items-center gap-4 px-4 py-3 text-left transition-colors hover:bg-zinc-50"
            >
              <span
                className={`w-6 flex-shrink-0 text-center font-heading text-lg font-bold ${MEDAL[i] || "text-zinc-300"}`}
                data-testid={`scoreboard-rank-${l.id}`}
              >
                {i + 1}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1 truncate text-sm font-medium text-zinc-900">
                  {l.name}
                  {l.rated < elements.length && (
                    <span className="ml-1 text-[10px] font-normal text-amber-600">· {l.rated}/{elements.length} rated</span>
                  )}
                </div>
                <div className="mt-1.5 h-1.5 w-full overflow-hidden bg-zinc-100">
                  <div className="h-full bg-[#b32233] transition-all" style={{ width: `${Math.min(100, l.score * 10)}%` }} />
                </div>
              </div>
              <span className="w-20 flex-shrink-0 text-right font-heading text-base font-bold text-[#b32233]" data-testid={`scoreboard-score-${l.id}`}>
                {l.score.toFixed(1)}
                <span className="text-xs font-normal text-zinc-400">/10</span>
              </span>
              <ChevronRight className="h-4 w-4 flex-shrink-0 text-zinc-300" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
